import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Dictionary, difference, omit, union } from 'lodash';

import { CompanyPreferencesApiClient } from '~/services/Http';

export interface CompanyPreferencesState {
  data?: object;
  isLoading: boolean;
  failedToLoad: boolean;
  isClearing: boolean;
  failedToClear: boolean;
  isUpdated: boolean;
  hasData: boolean;
}

export interface StoreState {
  companyPreferences: Dictionary<CompanyPreferencesState>;
  /**
   * Stores the list of the company setting keys that
   * were required by some components. When is undefined
   * means that all company settings should be requested.
   */
  requiredCompanyPreferences?: string[];
  /**
   * Stores the list of company setting keys that were
   * requested from the API. This list is updated
   * after the request (is not waiting for data).
   * When is undefined means that UI already has
   * requested all company settings from API.
   */
  requestedCompanyPreferences?: string[];
  isLoadingAllCompanyPreferences: boolean;
  failedToLoadAllCompanyPreferences: boolean;
  isClearingAllCompanyPreferences: boolean;
  failedToClearAllCompanyPreferences: boolean;
}

export const initialCompanyPreferencesState: CompanyPreferencesState = {
  isLoading: false,
  failedToLoad: false,
  isClearing: false,
  failedToClear: false,
  isUpdated: false,
  hasData: false,
};

export const initialCompanyPreferencesStoreState: StoreState = {
  companyPreferences: {},
  requiredCompanyPreferences: [],
  requestedCompanyPreferences: [],
  isLoadingAllCompanyPreferences: false,
  failedToLoadAllCompanyPreferences: false,
  isClearingAllCompanyPreferences: false,
  failedToClearAllCompanyPreferences: false,
};

export enum CompanyPreferencesActionKeyPrefixes {
  GET = 'companyPreferences/GET',
  CLEAR = 'companyPreferences/CLEAR',
}

export const getCompanyPreferences = createAsyncThunk<
  Dictionary<object>,
  string[] | undefined
>(
  CompanyPreferencesActionKeyPrefixes.GET,
  async (keys: string[] | undefined): Promise<Dictionary<object>> => {
    return await CompanyPreferencesApiClient.getCompanyPreferences(keys).then(
      (r) => r.data,
    );
  },
);

export const clearCompanyPreferences = createAsyncThunk(
  CompanyPreferencesActionKeyPrefixes.CLEAR,
  async (keys: string[]): Promise<void> => {
    if (!keys.length) {
      return;
    }

    await CompanyPreferencesApiClient.removeCompanyPreferences(keys);
  },
);

export const companyPreferencesSlice = createSlice({
  name: '~/data/companyPreferences',
  initialState: initialCompanyPreferencesStoreState,
  reducers: {
    requireCompanyPreferences: (
      state: StoreState,
      action: PayloadAction<string[] | undefined>,
    ) => {
      state.requiredCompanyPreferences = state.requestedCompanyPreferences
        ? state.requiredCompanyPreferences &&
          action.payload &&
          difference(
            union(state.requiredCompanyPreferences, action.payload),
            state.requestedCompanyPreferences,
          )
        : [];
    },
    updateCompanyPreferences: (
      state: StoreState,
      action: PayloadAction<Dictionary<object>>,
    ) => {
      Object.keys(action.payload).forEach((key) => {
        state.companyPreferences[key] = {
          ...initialCompanyPreferencesState,
          ...state.companyPreferences[key],
          data: {
            /**
             * Rewriting the data will allow us to much easily
             * update only a part of the data.
             * When needed to clear something from data, just
             * set it undefined.
             */
            ...state.companyPreferences[key]?.data,
            ...action.payload[key],
          },
          isUpdated: true,
        };
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyPreferences.pending, (state, action) => {
        if (!action.meta.arg) {
          state.requiredCompanyPreferences = [];
          state.requestedCompanyPreferences = undefined;
          state.isLoadingAllCompanyPreferences = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.companyPreferences[key] = {
              ...initialCompanyPreferencesState,
              ...state.companyPreferences[key],
              isLoading: true,
            };
          });

          state.requiredCompanyPreferences =
            state.requiredCompanyPreferences &&
            difference(state.requiredCompanyPreferences, action.meta.arg);

          state.requestedCompanyPreferences =
            state.requestedCompanyPreferences &&
            union(state.requestedCompanyPreferences, action.meta.arg);
        }
      })
      .addCase(getCompanyPreferences.fulfilled, (state, action) => {
        (action.meta.arg || Object.keys(action.payload)).forEach((key) => {
          state.companyPreferences[key] = {
            ...initialCompanyPreferencesState,
            hasData: true,
            data: action.payload[key],
          };
        });

        state.isLoadingAllCompanyPreferences = false;
        state.failedToLoadAllCompanyPreferences = false;
      })
      .addCase(getCompanyPreferences.rejected, (state, action) => {
        if (!action.meta.arg) {
          state.isLoadingAllCompanyPreferences = false;
          state.failedToLoadAllCompanyPreferences = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.companyPreferences[key] = {
              ...initialCompanyPreferencesState,
              ...state.companyPreferences[key],
              isLoading: false,
              failedToLoad: true,
            };
          });
        }
      })
      .addCase(clearCompanyPreferences.pending, (state, action) => {
        if (!action.meta.arg) {
          state.isClearingAllCompanyPreferences = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.companyPreferences[key] = {
              ...initialCompanyPreferencesState,
              ...state.companyPreferences[key],
              isClearing: true,
            };
          });
        }
      })
      .addCase(clearCompanyPreferences.fulfilled, (state, action) => {
        if (!action.meta.arg) {
          state.companyPreferences = {};
          state.isClearingAllCompanyPreferences = false;
          state.failedToClearAllCompanyPreferences = false;
        } else {
          state.companyPreferences = omit(
            state.companyPreferences,
            action.meta.arg,
          );
        }
      })
      .addCase(clearCompanyPreferences.rejected, (state, action) => {
        if (!action.meta.arg) {
          state.isClearingAllCompanyPreferences = false;
          state.failedToClearAllCompanyPreferences = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.companyPreferences[key] = {
              ...initialCompanyPreferencesState,
              ...state.companyPreferences[key],
              isClearing: false,
              failedToClear: true,
            };
          });
        }
      });
  },
});

export const { requireCompanyPreferences, updateCompanyPreferences } =
  companyPreferencesSlice.actions;
export const companyPreferencesReducer = companyPreferencesSlice.reducer;
